import type {
  AgentCommand,
  AgentEvent,
  AgentPhase,
  Capability,
  IncompatibleProtocolEvent,
  Message,
  ProtocolInfoEvent,
  RenderInvokedEvent,
} from "@skaile/workspaces/types";
import type {
  AgentClientLike,
  AgentStoreOptions,
  AgentStoreSnapshot,
  ConnectionStatus,
  SubscribableStore,
} from "./types.js";

const DEFAULT_PAGE_SIZE = 50;

/** Conservative equality for plain JSON data; unsupported or unverifiable values fail open. */
function isSameJsonValue<T>(a: T | undefined, b: T, conservativeCommandInput = false): boolean {
  const aObjects = new WeakSet<object>();
  const bObjects = new WeakSet<object>();

  const compare = (left: unknown, right: unknown): boolean => {
    if (left === null || right === null) return left === right;
    if (typeof left !== typeof right) return false;

    switch (typeof left) {
      case "string":
      case "boolean":
        return left === right;
      case "number":
        return (
          Number.isFinite(left) &&
          Number.isFinite(right) &&
          !Object.is(left, -0) &&
          !Object.is(right, -0) &&
          left === right
        );
      case "object":
        break;
      default:
        return false;
    }

    const leftObject = left as object;
    const rightObject = right as object;
    if (aObjects.has(leftObject) || bObjects.has(rightObject)) return false;
    if (conservativeCommandInput) {
      if (leftObject === rightObject || bObjects.has(leftObject) || aObjects.has(rightObject)) {
        return false;
      }
    }
    aObjects.add(leftObject);
    bObjects.add(rightObject);

    const leftIsArray = Array.isArray(leftObject);
    if (leftIsArray !== Array.isArray(rightObject)) return false;

    const leftPrototype = Object.getPrototypeOf(leftObject);
    const rightPrototype = Object.getPrototypeOf(rightObject);
    const leftHasPlainPrototype = leftIsArray
      ? leftPrototype === Array.prototype
      : leftPrototype === Object.prototype || leftPrototype === null;
    const rightHasPlainPrototype = leftIsArray
      ? rightPrototype === Array.prototype
      : rightPrototype === Object.prototype || rightPrototype === null;
    if (
      !leftHasPlainPrototype ||
      !rightHasPlainPrototype ||
      leftPrototype !== rightPrototype ||
      !Object.isExtensible(leftObject) ||
      !Object.isExtensible(rightObject)
    ) {
      return false;
    }

    const leftKeys = Reflect.ownKeys(leftObject);
    const rightKeys = Reflect.ownKeys(rightObject);
    if (
      leftKeys.length !== rightKeys.length ||
      leftKeys.some((key) => typeof key !== "string") ||
      rightKeys.some((key) => typeof key !== "string")
    ) {
      return false;
    }

    if (leftIsArray) {
      const leftLengthDescriptor = Object.getOwnPropertyDescriptor(leftObject, "length");
      const rightLengthDescriptor = Object.getOwnPropertyDescriptor(rightObject, "length");
      const leftLength = leftLengthDescriptor?.value;
      const rightLength = rightLengthDescriptor?.value;
      if (
        !leftLengthDescriptor ||
        !("value" in leftLengthDescriptor) ||
        !leftLengthDescriptor.writable ||
        leftLengthDescriptor.enumerable ||
        leftLengthDescriptor.configurable ||
        !rightLengthDescriptor ||
        !("value" in rightLengthDescriptor) ||
        !rightLengthDescriptor.writable ||
        rightLengthDescriptor.enumerable ||
        rightLengthDescriptor.configurable ||
        typeof leftLength !== "number" ||
        leftLength !== rightLength ||
        leftKeys.length !== leftLength + 1
      ) {
        return false;
      }

      for (let index = 0; index < leftLength; index++) {
        const key = String(index);
        const leftDescriptor = Object.getOwnPropertyDescriptor(leftObject, key);
        const rightDescriptor = Object.getOwnPropertyDescriptor(rightObject, key);
        if (
          !leftDescriptor?.enumerable ||
          !("value" in leftDescriptor) ||
          !leftDescriptor.writable ||
          !leftDescriptor.configurable ||
          !rightDescriptor?.enumerable ||
          !("value" in rightDescriptor) ||
          !rightDescriptor.writable ||
          !rightDescriptor.configurable ||
          !compare(leftDescriptor.value, rightDescriptor.value)
        ) {
          return false;
        }
      }
      return true;
    }

    for (let index = 0; index < leftKeys.length; index++) {
      const leftKey = leftKeys[index] as string;
      const rightKey = rightKeys[index] as string;
      if (leftKey !== rightKey) return false;

      const leftDescriptor = Object.getOwnPropertyDescriptor(leftObject, leftKey);
      const rightDescriptor = Object.getOwnPropertyDescriptor(rightObject, rightKey);
      if (
        !leftDescriptor?.enumerable ||
        !("value" in leftDescriptor) ||
        !leftDescriptor.writable ||
        !leftDescriptor.configurable ||
        !rightDescriptor?.enumerable ||
        !("value" in rightDescriptor) ||
        !rightDescriptor.writable ||
        !rightDescriptor.configurable ||
        !compare(leftDescriptor.value, rightDescriptor.value)
      ) {
        return false;
      }
    }
    return true;
  };

  try {
    const same = compare(a, b);
    if (!same || !conservativeCommandInput) return same;
    structuredClone(a);
    structuredClone(b);
    return true;
  } catch {
    return false;
  }
}

/**
 * Headless reactive store for agent conversation state.
 *
 * Framework-agnostic — works with React (`useSyncExternalStore`),
 * Vue (`shallowRef` + `subscribe`), or any other UI framework.
 *
 * Accepts a minimal transport interface (`send` + `onEvent`) so it works
 * with WebSocket, tRPC, SSE, or any other delivery mechanism.
 * @docLink packages/store/concepts#agent-store
 */
export class AgentStore implements SubscribableStore<AgentStoreSnapshot> {
  private readonly _options: AgentStoreOptions;
  private _messages: Message[];
  private _streamingText = "";
  private _cancelledText = "";
  private _status: ConnectionStatus = "idle";
  private _phase: AgentPhase = "idle";
  private _pendingQuestion: AgentStoreSnapshot["pendingQuestion"] = null;
  private _error: AgentStoreSnapshot["error"] = null;
  private _isAllLoaded: boolean;
  private _seq: number;
  private _streamingFlushed = true;
  /** Shared state per store namespace — new Map instance on every mutation. */
  private _sharedState: Map<string, Record<string, unknown>> = new Map();
  /** Protocol v2 capability registry — new Map instance on every mutation. */
  private _capabilities: Map<string, Capability> = new Map();
  /** Protocol v2 active render invocations — new Map instance on every mutation. */
  private _activeRenderInvocations: Map<string, RenderInvokedEvent> = new Map();
  /** Protocol v2 — most recent `protocol_info` event from the runner. */
  private _protocolInfo: ProtocolInfoEvent | null = null;
  /** Protocol v2 — latest incompatible-protocol notice. */
  private _incompatibleProtocol: IncompatibleProtocolEvent | null = null;

  private _snapshot: AgentStoreSnapshot | null = null;
  private readonly _listeners = new Set<() => void>();
  private readonly _unsubEvent: () => void;
  private readonly _unsubStatus: (() => void) | undefined;

  /**
   * @param options - Store configuration including transport and optional pagination support.
   */
  constructor(options: AgentStoreOptions) {
    this._options = options;
    this._messages = options.initialMessages ? [...options.initialMessages] : [];
    this._isAllLoaded = !options.loadMessages;
    this._seq = this._messages.reduce((max, m) => Math.max(max, m.seq), 0);

    this._unsubEvent = options.onEvent((event) => this._handleEvent(event));
    this._unsubStatus = options.onStatusChange?.((status) => this.setStatus(status));
  }

  // -- Reactive state --------------------------------------------------------

  /** Accumulated conversation messages, oldest first. */
  get messages(): readonly Message[] {
    return this._messages;
  }

  /** Text currently streaming from the agent. Empty when not streaming. */
  get streamingText(): string {
    return this._streamingText;
  }

  /** Current connection lifecycle state. */
  get status(): ConnectionStatus {
    return this._status;
  }

  /** Current agent execution phase (e.g. `"idle"`, `"thinking"`, `"executing"`). */
  get phase(): AgentPhase {
    return this._phase;
  }

  /** The pending question the agent is waiting for the user to answer, or null. */
  get pendingQuestion(): AgentStoreSnapshot["pendingQuestion"] {
    return this._pendingQuestion;
  }

  /** Most recent error, or null when no error is active. */
  get error(): AgentStoreSnapshot["error"] {
    return this._error;
  }

  /** Partial text that was visible when the user cancelled. Empty when not cancelled. */
  get cancelledText(): string {
    return this._cancelledText;
  }

  /** True when there are no more older messages to load via pagination. */
  get isAllLoaded(): boolean {
    return this._isAllLoaded;
  }

  // -- Commands --------------------------------------------------------------

  /**
   * Send a user prompt to the agent and optimistically update local state.
   *
   * Clears any previous error and cancelled text, sets phase to `"thinking"`,
   * and appends the prompt as a local message before forwarding to the agent.
   *
   * @param text - The user's prompt text.
   */
  prompt(text: string): void {
    // A prompt sent mid-turn (sender interjecting while the agent streams) is a
    // boundary: finalize the in-flight text before this prompt so the two don't
    // merge into one bubble. No-op at turn start (nothing streaming).
    this._flushStreaming();
    this._error = null;
    this._cancelledText = "";
    this._streamingFlushed = false;
    // A free-form prompt abandons any unanswered question: the turn it belonged
    // to is over. Left set, it would gate every status of the new turn.
    this._pendingQuestion = null;
    this._phase = "thinking";
    this._appendMessage({ type: "prompt", prompt: text }, "user");
    this._options.send({ type: "prompt", prompt: text });
    this._notify();
  }

  /**
   * Send a reply to the agent's pending question.
   *
   * Clears the pending question locally before forwarding the reply command.
   *
   * @param answer - The user's answer text.
   * @param question - The sub-question being answered, verbatim from its
   *   `question` event. Required for correctness in hosts that surface several
   *   queued questions at once ({@link pendingQuestion} tracks only the latest,
   *   so those hosts drive this from their own message history): without it the
   *   agent files the answer under its next unanswered sub-question, which
   *   mismatches any non-sequential reply.
   * @param requestId - Exact identity from the rendered question; never inferred from pending state.
   */
  reply(answer: string, question?: string, requestId?: string): void {
    // A reply to a stale card must not clear a newer request in this client.
    if (this._pendingQuestion?.requestId && this._pendingQuestion.requestId !== requestId) return;
    this._pendingQuestion = null;
    // Answering a pending question resumes the agent turn — restore the busy
    // phase (mirrors prompt()) so the thinking indicator reappears immediately
    // rather than after the agent's first resumed event lands.
    this._phase = "thinking";
    const command = {
      type: "reply",
      answer,
      ...(question !== undefined ? { question } : {}),
      ...(requestId !== undefined ? { requestId } : {}),
    } as const;
    this._appendMessage(command, "user");
    this._options.send(command);
    this._notify();
  }

  /**
   * Cancel the current agent turn.
   *
   * Preserves partial streaming text in {@link cancelledText} so the UI can
   * display it. Clears streaming state, sets phase to `"cancelling"` (not
   * yet confirmed idle — see {@link AgentPhase}), and sends a `cancel`
   * command to the agent.
   */
  cancel(): void {
    // Preserve partial streaming text so the UI can still display it
    this._cancelledText = this._streamingText;
    this._streamingText = "";
    this._streamingFlushed = true;
    this._phase = "cancelling";
    this._options.send({ type: "cancel" });
    this._notify();
  }

  /**
   * Update shared state for a store namespace.
   *
   * Sends a `state_update` command to the agent and optimistically
   * updates local state so the change is reflected immediately.
   *
   * @param store - Store namespace (e.g. "app", "component:render-123").
   * @param state - Full state snapshot (replaces previous).
   */
  updateState(store: string, state: Record<string, unknown>): void {
    this._options.send({ type: "state_update", store, state });
    if (isSameJsonValue(this._sharedState.get(store), state, true)) return;
    // Optimistic local update
    this._sharedState = new Map(this._sharedState);
    this._sharedState.set(store, state);
    this._notify();
  }

  /**
   * Send an arbitrary command to the agent.
   *
   * Use this for commands that don't have a dedicated convenience method
   * (e.g. `ui_interaction`). No local state changes — the command is
   * forwarded as-is to the transport.
   *
   * @param cmd - The command to send.
   */
  send(cmd: AgentCommand): void {
    this._options.send(cmd);
  }

  /**
   * Load the previous page of messages and prepend them to the current list.
   *
   * No-op when `loadMessages` was not provided at construction time or when
   * all history has already been loaded ({@link isAllLoaded} is true).
   * Sets {@link isAllLoaded} to true once the server returns fewer messages
   * than the configured page size.
   */
  async loadPreviousMessages(): Promise<void> {
    if (!this._options.loadMessages || this._isAllLoaded) return;

    const pageSize = this._options.pageSize ?? DEFAULT_PAGE_SIZE;
    const before = this._messages.length > 0 ? this._messages[0].seq : undefined;
    const older = await this._options.loadMessages({ before, limit: pageSize });

    if (older.length < pageSize) this._isAllLoaded = true;

    const existingIds = new Set(this._messages.map((m) => m.id));
    const unique = older.filter((m) => !existingIds.has(m.id));
    this._messages = [...unique, ...this._messages];

    this._notify();
  }

  // -- External control ------------------------------------------------------

  /**
   * Programmatically update the connection status.
   *
   * Called by transport wrappers that cannot use the `onStatusChange`
   * callback (e.g. tRPC subscriptions that track status externally).
   *
   * Writing the status the store already holds is a no-op: it neither rotates
   * the snapshot nor notifies listeners.
   *
   * @param status - The new connection status.
   */
  setStatus(status: ConnectionStatus): void {
    if (this._status === status) return;
    this._status = status;
    this._notify();
  }

  // -- useSyncExternalStore interface ----------------------------------------

  /**
   * Register a change listener. Called synchronously after every state change.
   *
   * Conforms to the `subscribe` parameter of React's `useSyncExternalStore`.
   *
   * @param listener - Callback invoked whenever state changes.
   * @returns Unsubscribe function — call to stop receiving notifications.
   */
  subscribe = (listener: () => void): (() => void) => {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  };

  /**
   * Return the current immutable state snapshot.
   *
   * A new object is produced lazily on the first call after each state change.
   * Subsequent calls return the same cached object until the next change.
   * Conforms to the `getSnapshot` parameter of React's `useSyncExternalStore`.
   */
  getSnapshot = (): AgentStoreSnapshot => {
    if (!this._snapshot) {
      this._snapshot = {
        messages: this._messages,
        streamingText: this._streamingText,
        cancelledText: this._cancelledText,
        status: this._status,
        phase: this._phase,
        pendingQuestion: this._pendingQuestion,
        error: this._error,
        isAllLoaded: this._isAllLoaded,
        sharedState: this._sharedState,
        capabilities: this._capabilities,
        activeRenderInvocations: this._activeRenderInvocations,
        protocolInfo: this._protocolInfo,
        incompatibleProtocol: this._incompatibleProtocol,
      };
    }
    return this._snapshot;
  };

  // -- Cleanup ---------------------------------------------------------------

  /**
   * Unsubscribe from transport events and clear all change listeners.
   *
   * Call this when the component or service that owns the store unmounts or
   * shuts down to prevent memory leaks.
   */
  dispose(): void {
    this._unsubEvent();
    this._unsubStatus?.();
    this._listeners.clear();
  }

  // -- Factory ---------------------------------------------------------------

  /**
   * Create an {@link AgentStore} connected to a duck-typed agent client.
   *
   * Adapts the client's `send`/`onEvent`/`offEvent` API to the
   * {@link StoreTransport} interface expected by the constructor, without
   * creating a hard runtime dependency on `@skaile/workspaces/client`.
   *
   * @param client - Any object implementing {@link AgentClientLike}.
   * @param opts - Optional store configuration.
   * @param opts.sessionId - Session ID for message envelopes (defaults to a new UUID).
   * @param opts.initialMessages - Messages to pre-populate the store with.
   *
   * @example
   * ```ts
   * const store = AgentStore.fromClient(agentClient, { sessionId: 'sess-123' })
   * ```
   */
  static fromClient(
    client: AgentClientLike,
    opts?: { sessionId?: string; initialMessages?: Message[] },
  ): AgentStore {
    return new AgentStore({
      sessionId: opts?.sessionId ?? crypto.randomUUID(),
      send: (cmd) => client.send(cmd),
      onEvent: (handler) => {
        client.onEvent(handler);
        return () => client.offEvent(handler);
      },
      initialMessages: opts?.initialMessages,
    });
  }

  // -- Internal --------------------------------------------------------------

  private _handleEvent(event: AgentEvent): void {
    switch (event.type) {
      case "text":
        this._cancelledText = "";
        this._streamingText += event.content;
        this._streamingFlushed = false;
        break;

      case "finished":
        this._cancelledText = "";
        this._flushStreaming();
        this._appendMessage(event);
        // A cancelled finished is synthetic (persisted immediately for reload-
        // durability) and precedes the driver's real abort/kill sequence — stay
        // "cancelling" until the later, genuine status event reports "idle".
        if (!event.cancelled) this._phase = "idle";
        this._pendingQuestion = null;
        break;

      case "status":
        // While cancelling, the driver's interrupt/unwind window can still emit
        // stale "working" statuses for already-buffered tool activity — ignore
        // anything but the genuine idle confirmation.
        if (this._phase === "cancelling" && event.phase !== "idle") return;
        // A parked question is the same shape: the driver re-emits the turn's
        // buffered tool activity — including one call for the question tool
        // itself — while nothing is actually running. Only the genuine idle
        // confirmation may move the phase until the user answers.
        if (this._pendingQuestion !== null && event.phase !== "idle") return;
        // The bridge re-emits phase:"working" on every tool call, so most status
        // events in a turn carry the phase we already hold. `_notify()` rotates
        // the snapshot, so notifying for them re-renders consumers for nothing.
        if (this._phase === event.phase) return;
        this._phase = event.phase;
        break;

      case "user_message":
        // A peer's message mid-turn is a boundary: finalize the in-flight text
        // so it renders before the interjection. Flush-only — the peer message
        // is delivered via the host's history, not this transient echo. The
        // flush is internally conditional, so an echo arriving while nothing is
        // streaming (the common case — peers talk between turns) changed no
        // state and notifying for it was a pure fanout.
        if (!this._flushStreaming()) return;
        break;

      case "question_reply":
        // An answer ends the parked turn whoever sent it, so it opens the status
        // gate the same way `reply()` does. A complete arm rather than a fall
        // through into the group below: sharing that body once let a tool_call
        // clear the flag, dropping the question card the user still had to
        // answer. No runner emits this event today — see this package's CLAUDE.md.
        this._pendingQuestion = null;
        this._flushStreaming();
        this._appendMessage(event);
        break;

      case "tool_call":
      case "tool_result":
      case "file_changed":
      case "subagent":
      case "dev_server":
      case "handoff":
        // Flush in-flight text first so each text block becomes its own
        // Message, interleaved with tool activity in order — otherwise text
        // before and after a tool call merges into one wall-of-text blob.
        this._flushStreaming();
        this._appendMessage(event);
        break;

      case "question":
        // A pending question is a human-in-the-loop pause — the agent is waiting
        // for the user, not working. Reset to idle (like finished/error) so any
        // phase-derived busy indicator clears until the user replies.
        this._flushStreaming();
        this._phase = "idle";
        this._pendingQuestion = event;
        this._appendMessage(event);
        break;

      case "error":
        this._error = { message: event.message, fatal: event.fatal };
        this._streamingText = "";
        this._streamingFlushed = true;
        this._phase = "idle";
        this._appendMessage(event);
        break;

      case "state_changed": {
        // Accumulate state per store namespace (notify only — no message).
        // Re-emits of unchanged state are routine, not exceptional: `serve.ts`
        // replays every namespace on connect (`buildStoreSnapshotReplay`), on a
        // seeded execution, on re-registering an already-registered store, and
        // immediately after registration — and the `xstate-store` adapter is
        // equality-free, so writing a key its current value emits too. Each of
        // those rebuilt the Map and rotated the snapshot for identical state.
        if (isSameJsonValue(this._sharedState.get(event.store), event.state)) return;
        this._sharedState = new Map(this._sharedState);
        this._sharedState.set(event.store, event.state);
        break;
      }

      // ── Protocol v2 ────────────────────────────────────────────────────────

      case "capability_register": {
        // TRANSIENT — accumulate v2 capabilities into the registry (no message).
        // The gateway replays the whole set on every (re)attach; a byte-identical
        // replay must not rotate the Map identity or notify, or a reconnect storm
        // compounds into an unbounded render loop in consumers.
        if (
          event.capabilities.every((cap) => isSameJsonValue(this._capabilities.get(cap.name), cap))
        ) {
          return;
        }
        this._capabilities = new Map(this._capabilities);
        for (const cap of event.capabilities) {
          this._capabilities.set(cap.name, cap);
        }
        break;
      }

      case "capability_deregister": {
        // TRANSIENT — drop capabilities by name. Names we never had are a no-op:
        // rebuilding the Map for them would notify with an identical registry.
        const present = event.names.filter((name) => this._capabilities.has(name));
        if (present.length === 0) return;
        this._capabilities = new Map(this._capabilities);
        for (const name of present) {
          this._capabilities.delete(name);
        }
        break;
      }

      case "render_invoked":
        // TRANSIENT — record latest render invocation (frontend pairs with
        // the matching `Capability.render` for placement).
        this._activeRenderInvocations = new Map(this._activeRenderInvocations);
        this._activeRenderInvocations.set(event.callId, event);
        break;

      case "protocol_info": {
        // TRANSIENT — runner announced its protocol version. It announces on
        // every attach, so tab mounts, StrictMode remounts and SSE reconnects
        // almost always carry the version we already hold. `ProtocolInfoEvent`
        // is nothing but `version`, and `ProtocolVersion` is exactly three
        // numbers, so the field comparison is complete, not conservative.
        //
        // A fresh announcement also retires any incompatibility notice: the
        // platform restarts the session on `incompatible_protocol`, and this
        // event is the replacement container introducing itself. Nothing cleared
        // `_incompatibleProtocol` before, so the restart banner was sticky for
        // the life of the store even though the platform's debug pane documents
        // this clear as already happening. Guarding without clearing would make
        // that permanent, since a repeat announcement would never get here.
        const held = this._protocolInfo?.version;
        const sameVersion =
          held !== undefined &&
          held.major === event.version.major &&
          held.minor === event.version.minor &&
          held.patch === event.version.patch;
        if (sameVersion && this._incompatibleProtocol === null) return;
        // Keep the held event object when the version is unchanged so consumers
        // with an effect keyed on `snapshot.protocolInfo` do not re-fire.
        if (!sameVersion) this._protocolInfo = event;
        this._incompatibleProtocol = null;
        break;
      }

      case "incompatible_protocol":
        // TRANSIENT — runner detected version drift; platform restarts session.
        this._incompatibleProtocol = event;
        break;

      case "capability_invoked":
        // Backend intercepts client-side invocations and dispatches them
        // through V2CapabilityRegistryService — frontends should not see
        // these. Drop silently if one slips through.
        return;

      case "resource_response":
      case "resources_available":
        // Transient — handled by ResourceClient, not AgentStore
        return;

      default:
        // The switch names 22 of `AgentEvent`'s 56 members. The other 34 —
        // `log_entry` above all, one per line of container output — hold no
        // AgentStore state, so falling through to the trailing `_notify()`
        // rotated the snapshot and re-rendered every consumer for state that
        // had not changed. Unlike the guarded cases this was not a replay
        // artifact: it happened on every single occurrence. Hosts that need
        // these events (log panels, mount indicators, typing bubbles) read them
        // off the transport upstream of the store, not out of the snapshot.
        //
        // Deliberately not an `assertNever` exhaustiveness check: that is a
        // compile error while 34 members are unnamed, and naming all 56 would
        // enumerate events the store holds no state for just to satisfy it.
        return;
    }

    this._notify();
  }

  /**
   * Finalize buffered streaming text as a `text` Message.
   *
   * @returns true when text was flushed; false when there was nothing to flush,
   *   so callers can skip a notify that would change nothing.
   */
  private _flushStreaming(): boolean {
    if (this._streamingFlushed || !this._streamingText) return false;
    this._appendMessage({ type: "text", content: this._streamingText });
    this._streamingText = "";
    this._streamingFlushed = true;
    return true;
  }

  private _appendMessage(payload: AgentCommand | AgentEvent, senderId?: string): void {
    this._seq++;
    this._messages = [
      ...this._messages,
      {
        id: crypto.randomUUID(),
        seq: this._seq,
        timestamp: new Date().toISOString(),
        sessionId: this._options.sessionId,
        ...(senderId ? { senderId } : {}),
        payload,
      },
    ];
  }

  private _notify(): void {
    this._snapshot = null;
    for (const listener of this._listeners) {
      listener();
    }
  }
}
